import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../shared/store/store';
import { sellerOrderService } from '../services/orderService';
import { SellerOrderState, SellerOrderStatus } from '../types/order.types';

const tok = (s: RootState) => s.auth.accessToken ?? '';

export const fetchSellerOrders = createAsyncThunk(
    'sellerOrder/fetchAll',
    async (params: Record<string, any>, { getState }) => {
        const { data } = await sellerOrderService.getOrders(params, tok(getState() as RootState));
        return data;
    }
);

export const fetchSellerOrderById = createAsyncThunk(
    'sellerOrder/fetchById',
    async (id: string, { getState }) => {
        const { data } = await sellerOrderService.getOrderById(id, tok(getState() as RootState));
        return data.order;
    }
);

export const updateSellerOrderStatus = createAsyncThunk(
    'sellerOrder/updateStatus',
    async ({ id, status }: { id: string; status: SellerOrderStatus }, { getState }) => {
        const { data } = await sellerOrderService.updateStatus(id, status, tok(getState() as RootState));
        return data.order;
    }
);

const initialState: SellerOrderState = {
    orders:         [],
    selected:       null,
    total:          0,
    page:           1,
    pages:          1,
    summary:        { PENDING: 0, CONFIRMED: 0, SHIPPED: 0, DELIVERED: 0, CANCELLED: 0 },
    totalRevenue:   0,
    loading:        false,
    error:          null,
    statusUpdating: false,
};

const orderSlice = createSlice({
    name: 'sellerOrder',
    initialState,
    reducers: {
        clearSelected: (s) => { s.selected = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSellerOrders.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchSellerOrders.fulfilled, (s, a) => {
                s.loading      = false;
                s.orders       = a.payload.orders;
                s.total        = a.payload.total;
                s.page         = a.payload.page;
                s.pages        = a.payload.pages;
                s.summary      = a.payload.summary;
                s.totalRevenue = a.payload.totalRevenue;
            })
            .addCase(fetchSellerOrders.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(fetchSellerOrderById.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchSellerOrderById.fulfilled, (s, a) => { s.loading = false; s.selected = a.payload; })
            .addCase(fetchSellerOrderById.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(updateSellerOrderStatus.pending,   (s) => { s.statusUpdating = true; s.error = null; })
            .addCase(updateSellerOrderStatus.fulfilled, (s, a) => {
                s.statusUpdating = false;
                const idx = s.orders.findIndex(o => o._id === a.payload._id);
                if (idx !== -1) s.orders[idx] = a.payload;
                if (s.selected?._id === a.payload._id) s.selected = a.payload;
            })
            .addCase(updateSellerOrderStatus.rejected,  (s, a) => { s.statusUpdating = false; s.error = a.error.message ?? null; });
    },
});

export const { clearSelected } = orderSlice.actions;
export default orderSlice.reducer;
